import { primitiveTypeUniq } from '@mews/fp';
import { formatFileSize } from '@optimus-web/core';
import { useGetServiceNamesByIdQuery } from '../../../common/queries/useGetServiceNamesByIdQuery';
import { includesLowerCase } from '../../../common/utils';
import { AttachmentsTableFilterValues } from '../AttachmentLibraryFilters';
import { AttachmentLibraryTableData } from '../types';
import { useCurrentEnterpriseAttachments } from '../../../common/hooks/useCurrentEnterpriseAttachments';

interface UseFilterAttachmentsTableDataProps {
    filters?: AttachmentsTableFilterValues;
    tableSearchValue?: string;
}

const filterBySearchValue = ({
    attachments,
    searchValue,
    serviceNamesById,
}: {
    attachments: AttachmentLibraryTableData;
    searchValue: string;
    serviceNamesById: Record<string, string>;
}): AttachmentLibraryTableData =>
    attachments.filter((attachment) => {
        const { name, serviceIds } = attachment;

        return (
            includesLowerCase(name, searchValue) ||
            serviceIds.some((id) => includesLowerCase(serviceNamesById[id], searchValue))
        );
    });

const filterByFilters = ({
    attachments,
    filters,
}: {
    attachments: AttachmentLibraryTableData;
    filters: AttachmentsTableFilterValues;
}): Omit<AttachmentLibraryTableData, 'type'> => {
    const { name: filterName, service: filterService } = filters;
    return attachments.filter((attachment) => {
        const { name, serviceIds } = attachment;
        const matchesName = filterName == null || filterName === '' || includesLowerCase(name, filterName);
        const matchesService = filterService == null || serviceIds.some((id) => filterService?.includes(id));
        return matchesName && matchesService;
    });
};

export const useFilteredAttachmentsTableData = ({
    filters,
    tableSearchValue,
}: UseFilterAttachmentsTableDataProps): AttachmentLibraryTableData => {
    const { data: services } = useGetServiceNamesByIdQuery();

    const attachments = useCurrentEnterpriseAttachments()?.map((attachment) => ({
        id: attachment.id,
        name: attachment.customName,
        serviceIds: attachment.services.map(({ id }) => id),
        type: attachment.mimeType,
        size: attachment ? formatFileSize(attachment.size) : '',
    }));

    const uniqueServiceIds = primitiveTypeUniq(attachments.flatMap((attachment) => attachment.serviceIds));

    const relevantServices = Object.entries(services)
        .filter(([id]) => uniqueServiceIds.includes(id))
        .reduce((acc, [id, name]) => ({ ...acc, [id]: name }), {});

    const dataFilteredByFilters = filters != null ? filterByFilters({ attachments, filters }) : attachments;

    return tableSearchValue != null
        ? filterBySearchValue({ attachments, searchValue: tableSearchValue, serviceNamesById: relevantServices })
        : dataFilteredByFilters;
};
